import React, { useState } from 'react';
import { TI_CHANNELS, TI_PRIORITIES, TI_STATUSES } from '../../triage/config';
import { getTriageCurrentUserId } from '../../triage/identity';
import { tiStaffById } from '../../triage/utils';
import type { TriageItem, TriageStatus } from '../../triage/types';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Button } from '../Button/Button';
import { TriageAssigneeAvatar } from './TriageAssigneeAvatar';
import { TriageAssigneeMenu } from './TriageAssigneeMenu';
import { TriageStatusMenu } from './TriageStatusMenu';

export interface TriageDetailHeaderProps {
  item: TriageItem;
  onChangeStatus: (id: number, status: TriageStatus) => void;
  onChangeAssignee: (id: number, assigneeId: string | null) => void;
}

export function TriageDetailHeader({ item, onChangeStatus, onChangeAssignee }: TriageDetailHeaderProps) {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  const assignee = tiStaffById(item.assigneeId);
  const isClosed = item.status === 'closed';
  const ch = TI_CHANNELS[item.channel];
  const pr = TI_PRIORITIES[item.priority] ?? TI_PRIORITIES.medium!;
  const statusCfg = TI_STATUSES[item.status];

  const primaryAction =
    item.status === 'open' || item.status === 'waiting'
      ? { label: 'Close task', next: 'closed' as TriageStatus, icon: 'check' as const, secondary: false }
      : item.status === 'closed'
        ? { label: 'Reopen', next: 'open' as TriageStatus, icon: 'refresh' as const, secondary: true }
        : null;

  return (
    <div
      className="v6-fade-up v6-d-0"
      style={{
        borderRadius: 'var(--r-lg)',
        border: '1px solid var(--cream-2)',
        boxShadow: 'var(--sh-1)',
        position: 'relative',
        zIndex: 2,
        marginBottom: 18,
        background: 'var(--cream-1)',
      }}
    >
      <div
        style={{
          height: 3,
          background: 'var(--grad-spectrum)',
          borderRadius: 'calc(var(--r-lg) - 1px) calc(var(--r-lg) - 1px) 0 0',
        }}
      />
      <div
        style={{
          borderRadius: '0 0 calc(var(--r-lg) - 1px) calc(var(--r-lg) - 1px)',
          padding: '18px 20px',
          background:
            'var(--brand-indigo-tint)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8, flexWrap: 'wrap' }}>
            <h1
              className="v6-display"
              style={{
                fontSize: 24,
                fontWeight: 600,
                letterSpacing: '-0.025em',
                color: 'var(--ink-0)',
                margin: 0,
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
              }}
            >
              {item.patient}
            </h1>
            {!isClosed ? (
              <span className={`v6-pill ${pr.pillClass}`} style={{ fontSize: 11, padding: '2px 9px', fontWeight: 600 }}>
                {item.priority === 'urgent' ? (
                  <span className="ti-urgent-dot" style={{ width: 6, height: 6, animation: 'none' }} />
                ) : null}
                {pr.label}
              </span>
            ) : null}
            {ch ? (
              <span
                className="ti-channel-chip"
                style={{ background: ch.bg, color: ch.color, fontSize: 11, padding: '2px 9px' }}
              >
                <Icon kind={ch.icon} size={11} stroke={2.2} />
                {ch.label}
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', letterSpacing: '-0.005em', marginBottom: 12 }}>
            {item.subject} <span style={{ color: 'var(--ink-3)' }}>· {item.time}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() =>
                  (statusCfg?.canTransitionTo.length ?? 0) > 0 && setStatusMenuOpen((o) => !o)
                }
                style={{
                  background: statusCfg?.bg,
                  color: statusCfg?.color,
                  border: `1px solid ${statusCfg?.bg}`,
                  borderRadius: 999,
                  padding: '4px 12px 4px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: (statusCfg?.canTransitionTo.length ?? 0) > 0 ? 'pointer' : 'default',
                  fontFamily: 'var(--font-body)',
                }}
              >
                <span className="ti-status-dot" style={{ background: statusCfg?.dot, width: 8, height: 8 }} />
                {statusCfg?.label}
                {(statusCfg?.canTransitionTo.length ?? 0) > 0 ? (
                  <Icon kind="down" size={10} stroke={2.2} />
                ) : null}
              </button>
              <TriageStatusMenu
                open={statusMenuOpen}
                currentStatus={item.status}
                onSelect={(next) => {
                  onChangeStatus(item.id, next);
                  setStatusMenuOpen(false);
                }}
                onClose={() => setStatusMenuOpen(false)}
                anchorStyle={{ left: 0, top: 'calc(100% + 4px)' }}
              />
            </div>

            <div style={{ position: 'relative' }}>
              {assignee ? (
                <button
                  type="button"
                  onClick={() => setAssigneeMenuOpen((o) => !o)}
                  className="ti-chip-btn ti-chip-btn-assigned"
                  style={{ padding: '3px 11px 3px 4px', fontSize: 12 }}
                >
                  <TriageAssigneeAvatar staff={assignee} size={22} />
                  <span style={{ fontWeight: 600 }}>{assignee.name}</span>
                  <Icon kind="down" size={10} stroke={2} color="var(--ink-3)" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setAssigneeMenuOpen((o) => !o)}
                  className="ti-chip-btn ti-chip-btn-empty"
                  style={{ padding: '4px 12px', fontSize: 12 }}
                >
                  <Icon kind="plus" size={12} stroke={2} />
                  <span>Assign</span>
                  <Icon kind="down" size={10} stroke={2} />
                </button>
              )}
              <TriageAssigneeMenu
                open={assigneeMenuOpen}
                currentAssigneeId={item.assigneeId}
                onSelect={(id) => {
                  onChangeAssignee(item.id, id);
                  setAssigneeMenuOpen(false);
                }}
                onClose={() => setAssigneeMenuOpen(false)}
                anchorStyle={{ left: 0, top: 'calc(100% + 4px)' }}
              />
            </div>

            {!assignee && getTriageCurrentUserId() ? (
              <button
                type="button"
                onClick={() => onChangeAssignee(item.id, getTriageCurrentUserId())}
                style={{
                  padding: '4px 11px',
                  borderRadius: 999,
                  background: 'color-mix(in srgb, var(--success) 7%, transparent)',
                  border: '1px solid var(--success-tint)',
                  color: 'color-mix(in srgb, var(--success) 80%, #000)',
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                Assign to me
              </button>
            ) : null}

            {isClosed ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 11.5,
                  color: 'var(--ink-3)',
                  fontStyle: 'italic',
                }}
              >
                <Icon kind="lock" size={11} stroke={2} />
                Read-only
              </span>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
          {primaryAction ? (
            <Button
              variant={primaryAction.secondary ? 'secondary' : 'primary'}
              size="sm"
              iconLeft={primaryAction.icon}
              onClick={() => onChangeStatus(item.id, primaryAction.next)}
            >
              {primaryAction.label}
            </Button>
          ) : null}
          <IconButton icon="dots" size={32} label="More" />
        </div>
      </div>
    </div>
  );
}
